import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '../utility/axios';
import { useTableChanges } from '../hooks/useWebSocket';

const CustomizationContext = createContext();

const DEFAULT_CUSTOMIZATION = {
    company_name: 'ttPush',
    company_website: '',
    logo_url: '',
    contact_email: 'support@ttpush.com',
    contact_github: 'https://github.com/ttpush',
    contact_facebook: 'https://facebook.com/ttpush',
    contact_instagram: 'https://instagram.com/ttpush',
    contact_youtube: 'https://youtube.com/ttpush',
    contact_blog: 'https://ttpush.com',
    contact_linkedin: 'https://linkedin.com/company/ttpush',
    contact_twitter: 'https://twitter.com/ttpush',
    layout_border_color: '#2c2c42',
    layout_border_text_color: '#FFFFFF',
    layout_border_menu_color: '#2c2c42',
    layout_border_menu_hover_color: '#3a3a52',
    layout_border_menu_text_color: '#FFFFFF',
    layout_border_menu_hover_text_color: '#FFFFFF',
    confirm_buttons_color: '#1890ff',
    confirm_buttons_hover_color: '#1677cc',
    confirm_buttons_text_color: '#FFFFFF',
    confirm_buttons_hover_text_color: '#FFFFFF',
    cancel_buttons_color: '#ff4d4f',
    cancel_buttons_hover_color: '#d9363e',
    cancel_buttons_text_color: '#FFFFFF',
    cancel_buttons_hover_text_color: '#FFFFFF',
    pills_color: '#f0f0f0',
    pills_text_color: '#333333',
    sidebar_menu_bg_color: '#1a1a2e',
    sidebar_menu_item_color: '#b3b3b3',
    sidebar_menu_item_hover_bg_color: '#5E5BFF',
    sidebar_menu_item_active_color: '#5E5BFF',
    sidebar_menu_opacity: '0.5',
    login_buttons_color: '#5E5BFF',
    login_buttons_text_color: '#FFFFFF',
    login_buttons_hover_color: '#4A47CC',
    login_buttons_hover_text_color: '#FFFFFF',
    signup_buttons_color: '#5E5BFF',
    signup_buttons_text_color: '#FFFFFF',
    signup_buttons_hover_color: '#4A47CC',
    signup_buttons_hover_text_color: '#FFFFFF',
    guest_buttons_color: '#FFFFFF',
    guest_buttons_text_color: '#5E5BFF',
    guest_buttons_hover_color: '#FFFFFF',
    guest_buttons_hover_text_color: '#4A47CC',
};

export const useCustomization = () => {
    const context = useContext(CustomizationContext);
    if (!context) {
        throw new Error('useCustomization must be used within a CustomizationProvider');
    }
    return context;
};

export const getThemePreviewStyleVars = (settings) => {
    if (!settings) return {};
    const s = { ...DEFAULT_CUSTOMIZATION, ...settings };
    return {
        '--theme-layout-border-color': s.layout_border_color,
        '--theme-layout-border-text-color': s.layout_border_text_color,
        '--theme-layout-border-menu-color': s.layout_border_menu_color,
        '--theme-layout-border-menu-hover-color': s.layout_border_menu_hover_color,
        '--theme-layout-border-menu-text-color': s.layout_border_menu_text_color,
        '--theme-layout-border-menu-hover-text-color': s.layout_border_menu_hover_text_color,
        '--theme-confirm-buttons-color': s.confirm_buttons_color,
        '--theme-confirm-buttons-hover-color': s.confirm_buttons_hover_color,
        '--theme-confirm-buttons-text-color': s.confirm_buttons_text_color,
        '--theme-confirm-buttons-hover-text-color': s.confirm_buttons_hover_text_color,
        '--theme-cancel-buttons-color': s.cancel_buttons_color,
        '--theme-cancel-buttons-hover-color': s.cancel_buttons_hover_color,
        '--theme-cancel-buttons-text-color': s.cancel_buttons_text_color,
        '--theme-cancel-buttons-hover-text-color': s.cancel_buttons_hover_text_color,
        '--theme-pills-color': s.pills_color,
        '--theme-pills-text-color': s.pills_text_color,
        '--theme-sidebar-menu-bg': s.sidebar_menu_bg_color,
        '--theme-sidebar-menu-item-color': s.sidebar_menu_item_color,
        '--theme-sidebar-menu-item-hover-bg': s.sidebar_menu_item_hover_bg_color,
        '--theme-sidebar-menu-item-active-color': s.sidebar_menu_item_active_color,
        '--theme-sidebar-menu-opacity': s.sidebar_menu_opacity || '0.5',
        '--theme-login-buttons-color': s.login_buttons_color,
        '--theme-login-buttons-text-color': s.login_buttons_text_color,
        '--theme-login-buttons-hover-color': s.login_buttons_hover_color,
        '--theme-login-buttons-hover-text-color': s.login_buttons_hover_text_color,
        '--theme-signup-buttons-color': s.signup_buttons_color,
        '--theme-signup-buttons-text-color': s.signup_buttons_text_color,
        '--theme-signup-buttons-hover-color': s.signup_buttons_hover_color,
        '--theme-signup-buttons-hover-text-color': s.signup_buttons_hover_text_color,
        '--theme-guest-buttons-color': s.guest_buttons_color,
        '--theme-guest-buttons-text-color': s.guest_buttons_text_color,
        '--theme-guest-buttons-hover-color': s.guest_buttons_hover_color,
        '--theme-guest-buttons-hover-text-color': s.guest_buttons_hover_text_color,
        '--header-background': s.layout_border_color,
        '--footer-background': s.layout_border_color,
        '--header-text-color': s.layout_border_text_color,
        '--footer-text-color': s.layout_border_text_color,
        '--nav-menu-bg': s.layout_border_menu_color,
        '--nav-menu-hover-bg': s.layout_border_menu_hover_color,
        '--nav-menu-text': s.layout_border_menu_text_color,
        '--nav-menu-hover-text': s.layout_border_menu_hover_text_color,
        '--primary-button-bg': s.confirm_buttons_color,
        '--confirm-button-bg': s.confirm_buttons_color,
        '--primary-button-hover-bg': s.confirm_buttons_hover_color,
        '--confirm-button-hover-bg': s.confirm_buttons_hover_color,
        '--primary-button-text': s.confirm_buttons_text_color,
        '--confirm-button-text': s.confirm_buttons_text_color,
        '--primary-button-hover-text': s.confirm_buttons_hover_text_color,
        '--confirm-button-hover-text': s.confirm_buttons_hover_text_color,
        '--cancel-button-bg': s.cancel_buttons_color,
        '--delete-button-bg': s.cancel_buttons_color,
        '--cancel-button-hover-bg': s.cancel_buttons_hover_color,
        '--delete-button-hover-bg': s.cancel_buttons_hover_color,
        '--cancel-button-text': s.cancel_buttons_text_color,
        '--delete-button-text': s.cancel_buttons_text_color,
        '--cancel-button-hover-text': s.cancel_buttons_hover_text_color,
        '--delete-button-hover-text': s.cancel_buttons_hover_text_color,
        '--badge-background': s.pills_color,
        '--badge-text-color': s.pills_text_color,
        '--pill-background': s.pills_color,
        '--pill-text-color': s.pills_text_color,
    };
};

export const CustomizationProvider = ({ children }) => {
    const [customization, setCustomization] = useState(DEFAULT_CUSTOMIZATION);
    const [loading, setLoading] = useState(true);

    const flattenCustomizationData = (data) => {
        if (data.branding_settings) {
            return {
                company_name: data.branding_settings.company_name,
                company_website: data.branding_settings.company_website,
                logo_url: data.branding_settings.logo_url,
                contact_email: data.contact_settings.contact_email,
                contact_github: data.contact_settings.contact_github,
                contact_facebook: data.contact_settings.contact_facebook,
                contact_instagram: data.contact_settings.contact_instagram,
                contact_youtube: data.contact_settings.contact_youtube,
                contact_blog: data.contact_settings.contact_blog,
                contact_linkedin: data.contact_settings.contact_linkedin,
                contact_twitter: data.contact_settings.contact_twitter,
                layout_border_color: data.theme_settings.layout_border_color,
                layout_border_text_color: data.theme_settings.layout_border_text_color,
                layout_border_menu_color: data.theme_settings.layout_border_menu_color,
                layout_border_menu_hover_color: data.theme_settings.layout_border_menu_hover_color,
                layout_border_menu_text_color: data.theme_settings.layout_border_menu_text_color,
                layout_border_menu_hover_text_color: data.theme_settings.layout_border_menu_hover_text_color,
                confirm_buttons_color: data.theme_settings.confirm_buttons_color,
                confirm_buttons_hover_color: data.theme_settings.confirm_buttons_hover_color,
                confirm_buttons_text_color: data.theme_settings.confirm_buttons_text_color,
                confirm_buttons_hover_text_color: data.theme_settings.confirm_buttons_hover_text_color,
                cancel_buttons_color: data.theme_settings.cancel_buttons_color,
                cancel_buttons_hover_color: data.theme_settings.cancel_buttons_hover_color,
                cancel_buttons_text_color: data.theme_settings.cancel_buttons_text_color,
                cancel_buttons_hover_text_color: data.theme_settings.cancel_buttons_hover_text_color,
                pills_color: data.theme_settings.pills_color,
                pills_text_color: data.theme_settings.pills_text_color,
                sidebar_menu_bg_color: data.theme_settings.sidebar_menu_bg_color || '#1a1a2e',
                sidebar_menu_item_color: data.theme_settings.sidebar_menu_item_color || '#b3b3b3',
                sidebar_menu_item_hover_bg_color: data.theme_settings.sidebar_menu_item_hover_bg_color || '#5E5BFF',
                sidebar_menu_item_active_color: data.theme_settings.sidebar_menu_item_active_color || '#5E5BFF',
                sidebar_menu_opacity: data.theme_settings.sidebar_menu_opacity || '0.5',
                login_buttons_color: data.theme_settings.login_buttons_color || '#5E5BFF',
                login_buttons_text_color: data.theme_settings.login_buttons_text_color || '#FFFFFF',
                login_buttons_hover_color: data.theme_settings.login_buttons_hover_color || '#4A47CC',
                login_buttons_hover_text_color: data.theme_settings.login_buttons_hover_text_color || '#FFFFFF',
                signup_buttons_color: data.theme_settings.signup_buttons_color || '#5E5BFF',
                signup_buttons_text_color: data.theme_settings.signup_buttons_text_color || '#FFFFFF',
                signup_buttons_hover_color: data.theme_settings.signup_buttons_hover_color || '#4A47CC',
                signup_buttons_hover_text_color: data.theme_settings.signup_buttons_hover_text_color || '#FFFFFF',
                guest_buttons_color: data.theme_settings.guest_buttons_color || '#FFFFFF',
                guest_buttons_text_color: data.theme_settings.guest_buttons_text_color || '#5E5BFF',
                guest_buttons_hover_color: data.theme_settings.guest_buttons_hover_color || '#FFFFFF',
                guest_buttons_hover_text_color: data.theme_settings.guest_buttons_hover_text_color || '#4A47CC',
            };
        }
        return data;
    };

    const applyTheme = (settings) => {
        const root = document.documentElement;
        
        // Apply CSS custom properties for all theme colors
        // Layout Border Section
        root.style.setProperty('--theme-layout-border-color', settings.layout_border_color);
        root.style.setProperty('--theme-layout-border-text-color', settings.layout_border_text_color);
        root.style.setProperty('--theme-layout-border-menu-color', settings.layout_border_menu_color);
        root.style.setProperty('--theme-layout-border-menu-hover-color', settings.layout_border_menu_hover_color);
        root.style.setProperty('--theme-layout-border-menu-text-color', settings.layout_border_menu_text_color);
        root.style.setProperty('--theme-layout-border-menu-hover-text-color', settings.layout_border_menu_hover_text_color);
        
        // Confirm Buttons Section
        root.style.setProperty('--theme-confirm-buttons-color', settings.confirm_buttons_color);
        root.style.setProperty('--theme-confirm-buttons-hover-color', settings.confirm_buttons_hover_color);
        root.style.setProperty('--theme-confirm-buttons-text-color', settings.confirm_buttons_text_color);
        root.style.setProperty('--theme-confirm-buttons-hover-text-color', settings.confirm_buttons_hover_text_color);
        
        // Cancel Buttons Section
        root.style.setProperty('--theme-cancel-buttons-color', settings.cancel_buttons_color);
        root.style.setProperty('--theme-cancel-buttons-hover-color', settings.cancel_buttons_hover_color);
        root.style.setProperty('--theme-cancel-buttons-text-color', settings.cancel_buttons_text_color);
        root.style.setProperty('--theme-cancel-buttons-hover-text-color', settings.cancel_buttons_hover_text_color);
        
        // Pills Section
        root.style.setProperty('--theme-pills-color', settings.pills_color);
        root.style.setProperty('--theme-pills-text-color', settings.pills_text_color);
        
        // Sidebar Menu Section
        root.style.setProperty('--theme-sidebar-menu-bg', settings.sidebar_menu_bg_color);
        root.style.setProperty('--theme-sidebar-menu-item-color', settings.sidebar_menu_item_color);
        root.style.setProperty('--theme-sidebar-menu-item-hover-bg', settings.sidebar_menu_item_hover_bg_color);
        root.style.setProperty('--theme-sidebar-menu-item-active-color', settings.sidebar_menu_item_active_color);
        root.style.setProperty('--theme-sidebar-menu-opacity', settings.sidebar_menu_opacity || '0.5');
        
        // Nav auth buttons (layout top navbar)
        root.style.setProperty('--theme-login-buttons-color', settings.login_buttons_color);
        root.style.setProperty('--theme-login-buttons-text-color', settings.login_buttons_text_color);
        root.style.setProperty('--theme-login-buttons-hover-color', settings.login_buttons_hover_color);
        root.style.setProperty('--theme-login-buttons-hover-text-color', settings.login_buttons_hover_text_color);
        root.style.setProperty('--theme-signup-buttons-color', settings.signup_buttons_color);
        root.style.setProperty('--theme-signup-buttons-text-color', settings.signup_buttons_text_color);
        root.style.setProperty('--theme-signup-buttons-hover-color', settings.signup_buttons_hover_color);
        root.style.setProperty('--theme-signup-buttons-hover-text-color', settings.signup_buttons_hover_text_color);
        root.style.setProperty('--theme-guest-buttons-color', settings.guest_buttons_color);
        root.style.setProperty('--theme-guest-buttons-text-color', settings.guest_buttons_text_color);
        root.style.setProperty('--theme-guest-buttons-hover-color', settings.guest_buttons_hover_color);
        root.style.setProperty('--theme-guest-buttons-hover-text-color', settings.guest_buttons_hover_text_color);
        
        // Apply specific mappings for UI elements:
        // Header/Navigation - uses layout border colors
        root.style.setProperty('--header-background', settings.layout_border_color);
        root.style.setProperty('--footer-background', settings.layout_border_color);
        root.style.setProperty('--header-text-color', settings.layout_border_text_color);
        root.style.setProperty('--footer-text-color', settings.layout_border_text_color);
        
        // Navigation menu items - uses layout border menu colors
        root.style.setProperty('--nav-menu-bg', settings.layout_border_menu_color);
        root.style.setProperty('--nav-menu-hover-bg', settings.layout_border_menu_hover_color);
        root.style.setProperty('--nav-menu-text', settings.layout_border_menu_text_color);
        root.style.setProperty('--nav-menu-hover-text', settings.layout_border_menu_hover_text_color);
        
        // Primary/Confirm buttons - uses confirm button colors
        root.style.setProperty('--primary-button-bg', settings.confirm_buttons_color);
        root.style.setProperty('--confirm-button-bg', settings.confirm_buttons_color);
        root.style.setProperty('--primary-button-hover-bg', settings.confirm_buttons_hover_color);
        root.style.setProperty('--confirm-button-hover-bg', settings.confirm_buttons_hover_color);
        root.style.setProperty('--primary-button-text', settings.confirm_buttons_text_color);
        root.style.setProperty('--confirm-button-text', settings.confirm_buttons_text_color);
        root.style.setProperty('--primary-button-hover-text', settings.confirm_buttons_hover_text_color);
        root.style.setProperty('--confirm-button-hover-text', settings.confirm_buttons_hover_text_color);
        
        // Cancel/Delete buttons - uses cancel button colors
        root.style.setProperty('--cancel-button-bg', settings.cancel_buttons_color);
        root.style.setProperty('--delete-button-bg', settings.cancel_buttons_color);
        root.style.setProperty('--cancel-button-hover-bg', settings.cancel_buttons_hover_color);
        root.style.setProperty('--delete-button-hover-bg', settings.cancel_buttons_hover_color);
        root.style.setProperty('--cancel-button-text', settings.cancel_buttons_text_color);
        root.style.setProperty('--delete-button-text', settings.cancel_buttons_text_color);
        root.style.setProperty('--cancel-button-hover-text', settings.cancel_buttons_hover_text_color);
        root.style.setProperty('--delete-button-hover-text', settings.cancel_buttons_hover_text_color);
        
        // Badges/Pills - uses pills colors
        root.style.setProperty('--badge-background', settings.pills_color);
        root.style.setProperty('--badge-text-color', settings.pills_text_color);
        root.style.setProperty('--pill-background', settings.pills_color);
        root.style.setProperty('--pill-text-color', settings.pills_text_color);
    };

    const fetchCustomization = useCallback(async () => {
        try {
            const response = await axios.get('/api/customization/');
            let customizationData;
            
            // Handle new API response structure
            if (response.data.success && response.data.customization) {
                customizationData = flattenCustomizationData(response.data.customization);
            } else if (response.data.success === false) {
                console.error('Failed to fetch customization:', response.data.error);
                customizationData = DEFAULT_CUSTOMIZATION; // Use defaults
            } else {
                // Legacy response structure
                customizationData = flattenCustomizationData(response.data);
            }
            
            setCustomization(customizationData);
            applyTheme(customizationData);
        } catch (error) {
            console.error('Failed to fetch customization settings:', error);
            // Apply default theme if fetch fails
            applyTheme(DEFAULT_CUSTOMIZATION);
        } finally {
            setLoading(false);
        }
    }, []);

    // Handle real-time customization changes
    const handleCustomizationChange = useCallback((change) => {
        if (change.operation === 'UPDATE' || change.operation === 'INSERT') {
            // Customization changed - refresh to apply new theme
            fetchCustomization();
        }
    }, [fetchCustomization]);

    // Subscribe to real-time customization table changes
    useTableChanges('customization', handleCustomizationChange);

    const updateCustomization = (newSettings) => {
        // Handle both flat and nested structures
        const flatSettings = flattenCustomizationData(newSettings);
        setCustomization(flatSettings);
        applyTheme(flatSettings);
    };

    useEffect(() => {
        fetchCustomization();
        
        // Set up global refresh function for the admin panel
        window.refreshCustomization = fetchCustomization;
        
        return () => {
            // Clean up global function
            delete window.refreshCustomization;
        };
    }, [fetchCustomization]);

    const value = {
        customization,
        loading,
        updateCustomization,
        refreshCustomization: fetchCustomization,
        applyTheme
    };

    return (
        <CustomizationContext.Provider value={value}>
            {children}
        </CustomizationContext.Provider>
    );
};
